// src/pages/home.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useHomeViewModel } from "../viewmodels/useHomeViewModel.ts";
import Seo from "../component/common/Seo.jsx";

import HomeSearchBar from "../component/common/SearchBar.jsx";
import BannerSlider from "../component/common/Banners.jsx"
import CategoryGrid from "../component/home/CategoryGrid.jsx";
import RelatedServicesCarousel from "../component/home/RelatedServicesCarousel.jsx";
import UmmahTrendingSection from "../component/home/UmmahTrendingSection.jsx";
import { HomeSkeleton } from "../component/common/Skeleton.jsx";
import HomeNoticeModal, { shouldShowNotice } from "../component/home/HomeNoticeModal.jsx";
import { navigateToTarget } from "../helpers/targetNavigator.js";

export default function Home() {
  const navigate = useNavigate();
  const vm = useHomeViewModel();

  // 홈 공지는 홈 진입당 한 번만. '오늘 하루 보지 않기' 는 모달이 판단한다.
  const [noticeOpen, setNoticeOpen] = useState(false);
  useEffect(() => {
    if (shouldShowNotice(vm.homeNotice)) setNoticeOpen(true);
  }, [vm.homeNotice]);

  return (
    <>
    <Seo
      title="Korehalal Trip | Korea Halal DMC & Muslim Travel"
      description="Korea Halal DMC for Muslim travelers — book halal tours, explore the Korea Halal Map, and discover halal delivery services across Korea."
      path="/home"
    />
    <div className="max-w-6xl mx-auto">
      <h1 className="sr-only">Korehalal Trip — Halal &amp; Muslim-Friendly Tours in Korea</h1>
      <HomeSearchBar q={vm.q} setQ={vm.onChangeQ} />

      {vm.recommendedKeywords.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {vm.recommendedKeywords.map((keyword, idx) => (
            <button
              key={keyword}
              type="button"
              onClick={() => navigate(`/search?q=${encodeURIComponent(keyword)}`)}
              className={`${idx >= 3 ? "hidden sm:inline-flex" : "inline-flex"} px-3 py-1.5 rounded-full border border-border-tertiary bg-bg-secondary text-body-md text-text-secondary hover:bg-bg-tertiary transition`}
            >
              {keyword}
            </button>
          ))}
        </div>
      ) : null}

      {vm.isLoading && <HomeSkeleton />}
      {vm.error && <p className="mt-6 text-body-md text-text-error">{vm.error}</p>}

      {!vm.isLoading && !vm.error ? (
        <>
          <BannerSlider
            className="mt-6"
            items={vm.mainBanners}
            aspectClassName="aspect-[21/9]"
            priority
            onClickItem={(banner) => navigateToTarget(banner, navigate, vm.categories)}
          />

          <CategoryGrid categories={vm.categories} />

          {vm.activeRelatedGroups.length > 0 ? (
            <>
              {vm.activeRelatedGroups.slice(0, 2).map((group) => (
                <RelatedServicesCarousel
                  key={group.id}
                  items={group.services}
                  title={group.name ?? "Related Services"}
                  onItemClick={(item) => {
                    navigate(`/service/${item.uuid}`);
                  }}
                />
              ))}

              {vm.hasCommunity ? (
                <UmmahTrendingSection
                  title={vm.ummahPosts?.name ?? "Trending in Ummah"}
                  posts={vm.ummahPosts?.posts ?? []}
                  onViewAll={() => navigate("/community")}
                />
              ) : null}

              {vm.activeRelatedGroups.length > 2
                ? vm.activeRelatedGroups.slice(2).map((group) => (
                  <RelatedServicesCarousel
                    key={group.id}
                    items={group.services}
                    title={group.name ?? "Related Services"}
                    onItemClick={(item) => {
                      navigate(`/service/${item.uuid}`);
                    }}
                  />
                ))
                : null}
            </>
          ) : null}

          {vm.activeSubBanners.length > 0 ? (
            <BannerSlider
              className="mt-10"
              items={vm.activeSubBanners}
              aspectClassName="aspect-[10/3]"
              onClickItem={(banner) => navigateToTarget(banner, navigate, vm.categories)}
            />
          ) : null}
        </>
      ) : null}
    </div>

    {noticeOpen && vm.homeNotice ? (
      <HomeNoticeModal
        notice={vm.homeNotice}
        categories={vm.categories}
        onClose={() => setNoticeOpen(false)}
      />
    ) : null}
    </>
  );
}
